import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, Plus, Edit, Trash2, Save, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RestaurantZonesManager = ({ token, language, restaurants, zones }) => {
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restZones, setRestZones] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ zone_id: '', zone_name: '', custom_fee: '', is_deliverable: true });

  const fetchRestZones = useCallback(async (restId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/restaurant-zones/${restId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRestZones(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => {
    if (selectedRestaurant) fetchRestZones(selectedRestaurant);
  }, [selectedRestaurant, fetchRestZones]);

  const handleSave = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/restaurant-zones`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_id: selectedRestaurant,
          zone_id: form.zone_id,
          zone_name: form.zone_name || zones.find(z => z.zone_id === form.zone_id)?.name || '',
          custom_fee: parseFloat(form.custom_fee) || 0,
          is_deliverable: form.is_deliverable
        })
      });
      if (res.ok) {
        toast.success(language === 'pt' ? 'Zona adicionada!' : 'Zone added!');
        setDialogOpen(false);
        fetchRestZones(selectedRestaurant);
      }
    } catch (err) { toast.error('Erro'); }
  };

  const handleDelete = async (rzId) => {
    if (!window.confirm(language === 'pt' ? 'Remover zona?' : 'Remove zone?')) return;
    await fetch(`${API_URL}/api/admin/restaurant-zones/${rzId}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchRestZones(selectedRestaurant);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-base font-bold text-[#004E64] flex items-center gap-2">
        <MapPin className="text-[#FF6B35]" size={20} />
        {language === 'pt' ? 'Zonas de Entrega por Restaurante' : 'Restaurant Delivery Zones'}
      </h2>

      <div className="flex items-center gap-3">
        <Select value={selectedRestaurant || ''} onValueChange={setSelectedRestaurant}>
          <SelectTrigger className="w-[280px] rounded-xl" data-testid="select-restaurant-zone">
            <SelectValue placeholder={language === 'pt' ? 'Escolha um restaurante' : 'Choose a restaurant'} />
          </SelectTrigger>
          <SelectContent>
            {restaurants.map(r => (
              <SelectItem key={r.restaurant_id} value={r.restaurant_id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedRestaurant && (
          <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" onClick={() => { setForm({ zone_id: '', zone_name: '', custom_fee: '', is_deliverable: true }); setDialogOpen(true); }} data-testid="add-restaurant-zone-btn">
            <Plus size={14} className="mr-1" /> {language === 'pt' ? 'Adicionar Zona' : 'Add Zone'}
          </Button>
        )}
      </div>

      {selectedRestaurant && (
        <Card className="rounded-2xl">
          <CardContent className="p-4">
            {restZones.length === 0 ? (
              <p className="text-sm text-[#9FA2B4] text-center py-4">{language === 'pt' ? 'Sem zonas personalizadas' : 'No custom zones'}</p>
            ) : (
              <div className="space-y-2">
                {restZones.map(rz => (
                  <div key={rz.rzone_id} className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded-xl" data-testid={`restaurant-zone-${rz.rzone_id}`}>
                    <div>
                      <p className="text-sm font-semibold text-[#004E64]">{rz.zone_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="bg-[#FF6B35]/10 text-[#FF6B35] text-xs">
                          <DollarSign size={10} className="mr-1" />{rz.custom_fee?.toFixed(2)} EUR
                        </Badge>
                        {rz.is_deliverable ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">{language === 'pt' ? 'Entrega Ativa' : 'Delivers'}</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-xs">{language === 'pt' ? 'Nao Entrega' : 'No Delivery'}</Badge>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(rz.rzone_id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{language === 'pt' ? 'Adicionar Zona' : 'Add Zone'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{language === 'pt' ? 'Zona' : 'Zone'}</Label>
              <Select value={form.zone_id} onValueChange={v => setForm({...form, zone_id: v, zone_name: zones.find(z => z.zone_id === v)?.name || ''})}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder={language === 'pt' ? 'Escolha...' : 'Choose...'} /></SelectTrigger>
                <SelectContent>
                  {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id}>{z.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{language === 'pt' ? 'Taxa Personalizada (EUR)' : 'Custom Fee (EUR)'}</Label>
              <Input type="number" value={form.custom_fee} onChange={e => setForm({...form, custom_fee: e.target.value})} className="rounded-xl mt-1" placeholder="3.00" step="0.50" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_deliverable} onCheckedChange={v => setForm({...form, is_deliverable: v})} />
              <Label className="text-sm">{language === 'pt' ? 'Entrega disponivel' : 'Delivery available'}</Label>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" data-testid="save-restaurant-zone-btn">
              <Save size={16} className="mr-2" /> {language === 'pt' ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantZonesManager;
